import { useState } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { Button, Typography } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import useAuth from "../hooks/useAuth";
import { ASSET_URI, PROXY } from "../lib/utils";
import { getLanguageDirection } from "../i18n/config";

const TeacherCourses = () => {
  const { accessToken } = useAuth();
  const { t, i18n } = useTranslation();
  const fetcher = async (url) => {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.message || t("teacherStudio.load_failed", "Courses could not be loaded."));
    return payload;
  };
  const { data, error, isLoading, mutate } = useSWR(
    accessToken ? `${PROXY}/teacher/courses/panel?limit=50&page=1&sortField=updatedAt&sortOrder=-1` : null,
    fetcher,
  );

  // The protected panel endpoint applies the ownership filter server-side.
  // Do not re-filter populated/legacy IDs in the browser, which can hide a
  // teacher's valid courses despite a successful API response.
  const courses = data?.courses || [];

  const getStatusLabel = (status) =>
    status === "published"
      ? t("teacherStudio.status_published", "Published")
      : t("teacherStudio.status_draft", "Draft");

  if (error) {
    return (
      <section dir={getLanguageDirection(i18n.language)} className="mx-auto mt-8 max-w-3xl px-4 pb-12 sm:px-6">
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
          <Typography variant="h5" className="text-red-800">{t("teacherStudio.load_failed", "Courses could not be loaded.")}</Typography>
          <Typography className="mt-2 text-red-700">{error.message}</Typography>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button color="red" variant="outlined" onClick={() => mutate()}>{t("teacherStudio.retry", "Retry")}</Button>
            <Link to="/teacher/courses/new"><Button color="indigo">{t("teacherStudio.create_course", "Create Course")}</Button></Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      dir={getLanguageDirection(i18n.language)}
      className="mx-auto mt-8 max-w-7xl px-4 pb-12 sm:px-6 lg:px-8"
    >
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Typography variant="h3" className="text-slate-900">
            {t("teacherStudio.title", "Teacher Studio")}
          </Typography>
          <Typography className="mt-2 text-slate-600">
            {t(
              "teacherStudio.subtitle",
              "Create courses, update your own content, and track uploads without leaving the client app.",
            )}
          </Typography>
        </div>
        <Link to="/teacher/courses/new">
          <Button color="indigo">{t("teacherStudio.create_course", "Create Course")}</Button>
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">{t("teacherStudio.stats_courses", "Courses")}</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{courses.length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">{t("teacherStudio.stats_published", "Published")}</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {courses.filter((course) => course.status === "published").length}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">{t("teacherStudio.stats_drafts", "Drafts")}</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {courses.filter((course) => course.status !== "published").length}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-10 text-center text-slate-500">
          {t("teacherStudio.loading", "Loading your courses...")}
        </div>
      ) : courses.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Typography variant="h5" className="text-slate-900">
            {t("teacherStudio.empty_title", "No teacher courses yet")}
          </Typography>
          <Typography className="mt-2 text-slate-500">
            {t(
              "teacherStudio.empty_description",
              "Start by creating your first course. You can add videos, files, and a thumbnail right after.",
            )}
          </Typography>
          <Link to="/teacher/courses/new" className="mt-5 inline-block">
            <Button color="indigo">{t("teacherStudio.create_course", "Create Course")}</Button>
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {courses.map((course) => (
            <div key={course._id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex gap-4">
                <div className="h-24 w-24 overflow-hidden rounded-2xl bg-slate-100">
                  {course.image ? (
                    <img
                      src={`${ASSET_URI}/${course.image}`}
                      alt={course?.title?.en || t("teacherStudio.course_image_alt", "Course")}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      crossOrigin="anonymous"
                    />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-semibold text-slate-900">
                    {course?.title?.[i18n.language] || course?.title?.en || course?.title?.pr || course?.title?.ps || t("teacherStudio.untitled_course", "Untitled course")}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">{course.slug || t("teacherStudio.no_slug", "No slug")}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                      {getStatusLabel(course.status)}
                    </span>
                    {course.isLocked ? <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">{t("teacherStudio.locked", "Locked")}</span> : null}
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                      {t("teacherStudio.video_count", {
                        count: course.lessonsCount || 0,
                        defaultValue_one: "{{count}} video",
                        defaultValue_other: "{{count}} videos",
                      })}
                    </span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                      {t("teacherStudio.enrollment_count", {
                        count: course.enrollmentsCount || 0,
                        defaultValue_one: "{{count}} enrollment",
                        defaultValue_other: "{{count}} enrollments",
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link to={`/teacher/courses/${course._id}`}>
                  <Button variant="outlined" color="blue-gray">
                    {course.isLocked ? t("teacherStudio.view_details", "View Details") : t("teacherStudio.edit_details", "Edit Details")}
                  </Button>
                </Link>
                <Link to={`/teacher/courses/${course._id}/content`}>
                  <Button color="indigo">{t("teacherStudio.manage_content", "Manage Content")}</Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default TeacherCourses;
